import {
  CandidateProfileDetailProps,
  CandidateProfileDetailType
} from 'modules/candidate-profile/types/CandidateProfileDetailProps';
import { usePathsV2 } from 'core/services/navigation/usePathsV2';
import {
  useGetCandidateProfileListQuery,
  useGetCandidateBatchQuery
} from 'generated-graphql/hooks/apollo-hooks';
import { extractQueryGetItem } from 'core/utils/apollo/extractGetItem';
import { HeaderBreadcrumb } from 'common/ui/Header';
import { Paths } from 'services/navigation/paths';
import { useRecoilValue } from 'recoil';
import { integrationAtom } from 'services/integration/atom';
import { formatAtsProviderName } from 'core/utils/formatAtsProviderName';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { getAnalysisTitle } from 'utils/getAnalysisTitle';

export const useCandidateProfileDetailNavigation = (
  props: CandidateProfileDetailProps
) => {
  const { navigateToPath } = usePathsV2();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const integration = useRecoilValue(integrationAtom);
  // --- Scroll Restoration Support ---
  // When navigating from the candidate table to this detail page, the link passes
  // the full origin URL (including search params) via location.state.from.
  // We store this so handleBack can navigate directly to it, preserving:
  // - Filter params (filterValues_xxx)
  // - Pagination params (page_xxx, pageSize_xxx)
  // - AI conversation ID (searchQueryId)
  //
  // This is essential for scroll restoration because the scroll position is keyed
  // by the full URL. If we used navigate(-1), the URL might not include all params
  // (browser history behavior varies), breaking scroll restoration.
  const previousPathRef = useRef<string | null>(null);

  useEffect(() => {
    const state = location.state as { from?: string } | null;
    if (state?.from) {
      previousPathRef.current = state.from;
    }
  }, [location]);

  const talentPoolId =
    props.type ===
      CandidateProfileDetailType.TALENT_POOL_CANDIDATE_PROFILE_DETAILS ||
    props.type ===
      CandidateProfileDetailType.TALENT_POOL_ADD_CANDIDATES_CANDIDATE_PROFILE_DETAILS ||
    props.type ===
      CandidateProfileDetailType.ANALYSE_TALENT_POOL_CANDIDATE_PROFILE_DETAILS
      ? props.talentPoolId
      : undefined;

  const candidateBatchId =
    props.type ===
      CandidateProfileDetailType.ANALYSE_CANDIDATE_PROFILE_DETAILS ||
    props.type ===
      CandidateProfileDetailType.ANALYSE_TALENT_POOL_CANDIDATE_PROFILE_DETAILS
      ? props.candidateBatchId
      : undefined;

  const { data: talentPoolData } = useGetCandidateProfileListQuery({
    variables: { id: talentPoolId ?? '' },
    skip: !talentPoolId
  });

  const { data: candidateBatchData } = useGetCandidateBatchQuery({
    variables: { id: candidateBatchId ?? '' },
    skip: !candidateBatchId
  });

  const talentPool = extractQueryGetItem(talentPoolData);
  const candidateBatch = extractQueryGetItem(candidateBatchData);

  const analysisName = getAnalysisTitle(candidateBatch, 'Analysis');
  const atsProviderName = formatAtsProviderName(integration?.provider);

  const getBreadcrumbs = (): HeaderBreadcrumb[] => {
    switch (props.type) {
      case CandidateProfileDetailType.CANDIDATE_PROFILE_DETAILS:
        return [
          {
            name: 'Candidates',
            action: () => navigateToPath.CANDIDATE_PROFILE_LIST({})
          }
        ];
      case CandidateProfileDetailType.TALENT_POOL_CANDIDATE_PROFILE_DETAILS:
        return [
          {
            name: 'Candidates',
            action: () => navigateToPath.CANDIDATE_PROFILE_LIST({})
          },
          {
            name: talentPool?.title || 'Talent Pool',
            action: () =>
              navigateToPath.TALENT_POOL_DETAILS({
                talentPoolId: props.talentPoolId
              })
          }
        ];
      case CandidateProfileDetailType.TALENT_POOL_ADD_CANDIDATES_CANDIDATE_PROFILE_DETAILS:
        return [
          {
            name: 'Candidates',
            action: () => navigateToPath.CANDIDATE_PROFILE_LIST({})
          },
          {
            name: talentPool?.title || 'Talent Pool',
            action: () =>
              navigateToPath.TALENT_POOL_DETAILS({
                talentPoolId: props.talentPoolId
              })
          },
          {
            name: 'Add Candidates',
            action: () =>
              navigateToPath.TALENT_POOL_ADD_CANDIDATES({
                talentPoolId: props.talentPoolId
              })
          }
        ];
      case CandidateProfileDetailType.ANALYSE_CANDIDATE_PROFILE_DETAILS:
        return [
          {
            name: 'Analyse',
            path: Paths.ANALYSE
          },
          {
            name: analysisName,
            path: `${Paths.ANALYSE}/${props.candidateBatchId}`
          },
          {
            name: `Source from ${atsProviderName}`,
            action: () =>
              navigateToPath.ANALYSE_CANDIDATE_PROFILE_LIST({
                candidateBatchId: props.candidateBatchId
              })
          }
        ];
      case CandidateProfileDetailType.ANALYSE_TALENT_POOL_CANDIDATE_PROFILE_DETAILS:
        return [
          {
            name: 'Analyse',
            path: Paths.ANALYSE
          },
          {
            name: analysisName,
            path: `${Paths.ANALYSE}/${props.candidateBatchId}`
          },
          {
            name: `Source from ${atsProviderName}`,
            action: () =>
              navigateToPath.ANALYSE_CANDIDATE_PROFILE_LIST({
                candidateBatchId: props.candidateBatchId
              })
          },
          {
            name: talentPool?.title || 'Talent Pool',
            action: () =>
              navigateToPath.ANALYSE_TALENT_POOL_DETAILS({
                candidateBatchId: props.candidateBatchId,
                talentPoolId: props.talentPoolId
              })
          }
        ];
    }
  };

  // Navigate back to the candidate table, preserving scroll position.
  //
  // Why we use previousPathRef instead of navigate(-1):
  // - navigate(-1) uses browser history, which may not preserve all URL params
  // - The scroll restoration hook keys positions by full URL (path + search params)
  // - If params are missing, the saved scroll position won't be found
  //
  // Flow: User clicks candidate -> saves scroll + stores origin URL in state.from
  //       User clicks back -> navigates to stored URL -> scroll restoration runs
  const handleBack = () => {
    if (previousPathRef.current) {
      // Merge any params added on this page (e.g., sessionId from "Invite to Apply")
      // into the back-navigation URL so they aren't lost.
      const sessionId = searchParams.get('sessionId');
      if (sessionId) {
        const url = new URL(previousPathRef.current, window.location.origin);
        url.searchParams.set('sessionId', sessionId);
        navigate(url.pathname + url.search, { replace: true });
      } else {
        navigate(previousPathRef.current, { replace: true });
      }
      return;
    }

    // Fallback: If no stored path (e.g., user landed directly on detail page),
    // use browser history. Scroll won't restore but navigation still works.
    navigate(-1);
  };

  return {
    breadcrumbs: getBreadcrumbs(),
    handleBack
  };
};
